// API service for dental certificates management
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class DentalCertificateService {
  // Create new dental certificate
  static async createDentalCertificate(certificateData, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/dental-certificates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(certificateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error creating dental certificate:', error);
      throw error;
    }
  }

  // Get dental certificate by ID
  static async getDentalCertificateById(certificateId, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/dental-certificates/${certificateId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching dental certificate:', error);
      throw error;
    }
  }

  // Get dental certificates by patient
  static async getDentalCertificatesByPatient(patientId, token, options = {}) {
    try {
      const { limit = 50, lastKey, status = 'active', includeArchived = false } = options;
      
      const queryParams = new URLSearchParams({
        limit: limit.toString(),
        status,
        includeArchived: includeArchived.toString()
      });

      if (lastKey) {
        queryParams.append('lastKey', JSON.stringify(lastKey));
      }

      const response = await fetch(
        `${API_BASE_URL}/dental-certificates/patient/${patientId}?${queryParams}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        certificates: data.data || [],
        pagination: data.pagination || {}
      };
    } catch (error) {
      console.error('Error fetching patient dental certificates:', error);
      throw error;
    }
  }

  // Get all dental certificates
  static async getAllDentalCertificates(token, limit = 50, lastKey = null) {
    try {
      const queryParams = new URLSearchParams({
        limit: limit.toString()
      });

      if (lastKey) {
        queryParams.append('lastKey', JSON.stringify(lastKey));
      }

      const response = await fetch(`${API_BASE_URL}/dental-certificates?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        certificates: data.data || [],
        pagination: data.pagination || {}
      };
    } catch (error) {
      console.error('Error fetching dental certificates:', error);
      throw error;
    }
  }

  // Update dental certificate
  static async updateDentalCertificate(certificateId, certificateData, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/dental-certificates/${certificateId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(certificateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error updating dental certificate:', error);
      throw error;
    }
  }

  // Delete dental certificate (soft delete)
  static async deleteDentalCertificate(certificateId, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/dental-certificates/${certificateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting dental certificate:', error);
      throw error;
    }
  }

  // Archive dental certificate
  static async archiveDentalCertificate(certificateId, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/dental-certificates/${certificateId}/archive`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error archiving dental certificate:', error);
      throw error;
    }
  }

  // Restore dental certificate
  static async restoreDentalCertificate(certificateId, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/dental-certificates/${certificateId}/restore`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error restoring dental certificate:', error);
      throw error;
    }
  }

  // Search dental certificates
  static async searchDentalCertificates(query, token, limit = 50) {
    try {
      const queryParams = new URLSearchParams({
        query,
        limit: limit.toString()
      });

      const response = await fetch(`${API_BASE_URL}/dental-certificates/search?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Error searching dental certificates:', error);
      throw error;
    }
  }

  // Get dental certificate statistics
  static async getDentalCertificateStats(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/dental-certificates/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching dental certificate stats:', error);
      throw error;
    }
  }
}

export default DentalCertificateService;
